import { useCallback } from 'react';
import { Form, getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { invariant } from '@e-socna/utils';
import type { EMPTY_STRING } from '@e-socna/empty';
import type { SectionTypeId } from '@e-socna/data-layer';

import { getTouchedError } from '../../utils/get-touched-error.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { IdSelect } from '../../components/IdSelect';
import type { IdOption } from '../../components/IdSelect';
import type { SectionTemplatePage_OneSectionTemplate } from '../../pages/SectionTemplate/hooks/useFindOneSectionTemplateQuery.hook';

interface Props {
  readonly onCancel?: () => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly submitLabel: string;
  readonly title: string;
}

export const SectionTemplateInfoFormFields = ({
  onCancel: handleCancel,
  setMustBlockAction,
  showCancelButton,
  submitLabel,
  title,
}: Readonly<Props>) => {
  const { ui } = useUiContext();
  const { sectionTypes } = useReferencesContext();

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit: formikSubmit,
    isSubmitting,
    isValid,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<SectionTemplatePage_OneSectionTemplate>();

  const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

  invariant('code' in values.codingConfig, 'Unexpected codingConfig type');

  const handleTypeIdChange = useCallback(
    (id: SectionTypeId | typeof EMPTY_STRING) => {
      void setFieldValue('typeId', id);
    },
    [setFieldValue],
  );

  return (
    <Form
      onSubmit={formikSubmit}
      // eslint-disable-next-line react/forbid-component-props
      style={FORM_LAYOUT_STYLE}
    >
      <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
      <FormLayout
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              disabled={!isValid}
              loading={isLoading}
              size="large"
              type="submit"
              variant="contained"
            >
              {submitLabel}
            </Button>
            {!!showCancelButton && (
              <Button onClick={handleCancel} size="large" variant="outlined">
                Annuler
              </Button>
            )}
          </Stack>
        }
        title={title}
      >
        <Grid container spacing={2}>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <TextField
              error={
                !!(
                  getIn(errors, 'codingConfig.code') &&
                  getIn(touched, 'codingConfig.code')
                )
              }
              fullWidth
              helperText={getTouchedError(errors, touched, 'codingConfig.code')}
              id="section-template-coding-config-code"
              label="Code"
              name="codingConfig.code"
              onBlur={handleBlur}
              onChange={handleChange}
              required
              type="text"
              value={values.codingConfig.code}
              variant="outlined"
            />
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <TextField
              error={!!(errors.label && touched.label)}
              fullWidth
              helperText={getTouchedError(errors, touched, 'label')}
              id="section-template-label"
              label="Libellé"
              name="label"
              onBlur={handleBlur}
              onChange={handleChange}
              required
              type="text"
              value={values.label}
              variant="outlined"
            />
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <IdSelect<SectionTypeId, IdOption<SectionTypeId>>
              error={!!(errors.typeId && touched.typeId)}
              helperText={
                !!(errors.typeId && touched.typeId) &&
                (getIn(errors, 'typeId') as string)
              }
              id="section-template-type-id"
              label="Type de Section"
              name="typeId"
              onBlur={handleBlur}
              onChange={handleTypeIdChange}
              options={sectionTypes.map((sectionType) => ({
                id: sectionType._id,
                label: sectionType.label,
              }))}
              required
              value={values.typeId as SectionTypeId}
            />
          </Grid>
        </Grid>
      </FormLayout>
    </Form>
  );
};
